<template>
  <div class="giftcard">
    
<FormKitLazyProvider>
<FormKit
      v-if="product?.gift_card_options?.length"
      id="giftcard-options-form"
      type="form"
      :actions="false"
      name="giftcard-options-form"
      @input="collectWrapper"
    >
      <component
        :is="fieldTypes[option.__typename]"
        v-for="option in options"
        :key="option.option_id"
        :option="option"
      />
    </FormKit>
</FormKitLazyProvider>

  </div>
</template>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import type { MagentoGiftCardProduct, MagentoCartItemInput } from '~/app/types/magento'

export interface Props {
  product: MagentoGiftCardProduct
  hasError?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  selected: Record<string, MagentoCartItemInput>
}>()

const cartItemData = ref<Record<string, MagentoCartItemInput>>({})
const formIsValid = shallowRef<boolean>(false)

const collectWrapper = useDebounceFn((item: Record<string, unknown>) => {
  collect(item)
}, 120)

const collect = (item: Record<string, unknown>) => {
  formIsValid.value = getNode('giftcard-options-form').context?.state?.valid

  const selectedOptions: string[] = []
  if (item.giftcard_amounts) {
    selectedOptions.push(item.giftcard_amounts as string)
  }

  const enteredOptions = Object.entries(item)
    .filter(([key]) => key !== 'giftcard_amounts')
    .map(([uid, value]) => ({
      uid,
      value: value ? (value as string) : '',
    }))

  cartItemData.value = {
    data: {
      quantity: 1,
      sku: props.product.sku,
      selected_options: selectedOptions,
      entered_options: enteredOptions,
    },
  }

  selected()
}

const selected = () => {
  emit('selected', { [props.product.sku]: !formIsValid.value ? null : cartItemData.value })
}

watch(() => props.hasError, (newValue) => {
  if (newValue === true) {
    const node = getNode('giftcard-options-form')
    if (node) {
      node.submit()
    }
  }
})

const options = computed(() => {
  const giftCardOptions = props.product?.gift_card_options || []
  const amountsOption = props.product?.giftcard_amounts
    ? [{
        __typename: 'giftcardAmounts',
        option_id: 'giftcard_amounts',
        title: 'Amount',
        required: true,
        amounts: props.product.giftcard_amounts,
      }]
    : []

  return [...amountsOption, ...giftCardOptions]
})

const fieldTypes = {
  giftcardAmounts: defineAsyncComponent(() => import('~layers/app/components/Product/GiftCard/AmountOption.vue')),
  CustomizableFieldOption: defineAsyncComponent(() => import('~layers/app/components/Product/GiftCard/FieldOption.vue')),
}
</script>

<style>
@import url("~layers/app/components/Product/GiftCard/GiftCard.css");
</style>
